import type { FunctionComponent, PropsWithChildren } from 'react';
import { useCallback } from 'react';

import { JobArchitecturePermissionsContextProvider } from '../context/JobArchitecturePermissionsContext';
import {
    ActiveThreadStoreProvider,
    useActiveThreadStore,
} from '../job-profile-section/comments/ActiveThreadStoreProvider';
import { CommentsSection } from '../job-profile-section/comments/CommentsSection';
import { CommentsScopeProvider } from '../job-profile-section/comments/comments-scope-context';
import { useSidepanelCommentLanguages } from '../job-profile-section/comments/use-sidepanel-comment-languages';
import { Details as JobProfileDetails } from '../job-profile-section/Details';
import { JobArchitectureScopeProvider } from '../url-navigation/job-architecture-scope';
import type { JsaProfileDetailTilePayload } from '../url-tile-payloads';
import { useCommentsRailReveal } from './use-comments-rail-reveal';
import { useJobArchitectureUrlTileActions } from './use-job-architecture-url-tile-actions';

type JobProfileTileProps = {
    readonly close: () => void;
    readonly payload: JsaProfileDetailTilePayload;
};

export const JobProfileTileProviderStack: FunctionComponent<PropsWithChildren<{ readonly architectureId: string }>> = ({
    architectureId,
    children,
}) => (
    <JobArchitectureScopeProvider architectureId={architectureId}>
        <JobArchitecturePermissionsContextProvider>{children}</JobArchitecturePermissionsContextProvider>
    </JobArchitectureScopeProvider>
);

const JobProfileTileWithComments: FunctionComponent<JobProfileTileProps> = ({ close, payload }) => {
    const isCommentsOpen = useActiveThreadStore((state) => state.isCommentsOpen);
    const setIsCommentsOpen = useActiveThreadStore((state) => state.setIsCommentsOpen);
    const toggleCommentsOpen = useActiveThreadStore((state) => state.toggleCommentsOpen);
    const { updateJobProfileTileLanguage } = useJobArchitectureUrlTileActions();

    useCommentsRailReveal(isCommentsOpen);

    const availableLanguages = useSidepanelCommentLanguages({
        architectureId: payload.architectureId,
        itemId: payload.profileId,
        itemType: payload.itemType,
        version: payload.version,
    });

    const onCloseComments = useCallback(() => setIsCommentsOpen(false), [setIsCommentsOpen]);

    const commentsAside = useCallback(
        ({ commentResolutionDisabled }: { readonly commentResolutionDisabled: boolean }) =>
            isCommentsOpen ? (
                <CommentsSection
                    architectureId={payload.architectureId}
                    availableLanguages={availableLanguages}
                    commentResolutionDisabled={commentResolutionDisabled}
                    itemId={payload.profileId}
                    language={payload.preferredLanguage ?? 'en-US'}
                    version={payload.version}
                    onClose={onCloseComments}
                    onSwitchLanguage={updateJobProfileTileLanguage}
                />
            ) : null,
        [
            availableLanguages,
            isCommentsOpen,
            onCloseComments,
            payload.architectureId,
            payload.preferredLanguage,
            payload.profileId,
            payload.version,
            updateJobProfileTileLanguage,
        ],
    );

    return (
        <JobProfileDetails
            architectureId={payload.architectureId}
            commentsAside={commentsAside}
            isArchived={payload.isArchived}
            isCommentsOpen={isCommentsOpen}
            isOpen={true}
            itemId={payload.profileId}
            itemType={payload.itemType}
            mode={payload.mode}
            preferredLanguage={payload.preferredLanguage}
            version={payload.version}
            onClose={close}
            onToggleComments={toggleCommentsOpen}
        />
    );
};

export const JobProfileTile: FunctionComponent<{
    readonly close: () => void;
    readonly payload: JsaProfileDetailTilePayload;
}> = ({ close, payload }) => (
    <JobProfileTileProviderStack architectureId={payload.architectureId}>
        <CommentsScopeProvider
            architectureId={payload.architectureId}
            itemId={payload.profileId}
            language={payload.preferredLanguage ?? 'en-US'}
            version={payload.version}
        >
            <ActiveThreadStoreProvider initialIsCommentsOpen={payload.isCommentsOpen === true}>
                <JobProfileTileWithComments close={close} payload={payload} />
            </ActiveThreadStoreProvider>
        </CommentsScopeProvider>
    </JobProfileTileProviderStack>
);
